import React, { useRef, useState } from 'react';

import { Botao, Chip, DataList, Modal, Nota } from '../../../ui';
import Icone from '../../../ui/primitivos/Icone';

import { ResultadoImportacaoCsv, importarUsuariosCsv } from '../../../api/auth/authApi';

/**
 * Importação de usuários por CSV (at-68).
 *
 * A rota existia no backend desde sempre e **nunca teve tela** — e era **pública**, o
 * que significa que qualquer pessoa na internet criava usuários com uma senha temporária
 * conhecida. O guard entrou nesta mesma entrega.
 *
 * ⚠️ O formato do arquivo é **escrito na tela**, não presumido: os cabeçalhos são
 * casados por nome exato pelo backend, e a célula de cooperativas usa vírgula por dentro
 * — que é o mesmo separador do CSV, então precisa de aspas. Sem dizer isso, o TI monta a
 * planilha errada e descobre linha a linha.
 *
 * ⚠️ O backend devolve **um resultado por linha** e nunca aborta o arquivo: uma linha com
 * e-mail repetido falha sozinha. Os contadores "N criados · M com erro" são calculados
 * aqui, porque a resposta é um array sem resumo.
 */

interface ModalImportarUsuariosCsvProps {
  aberto: boolean;
  aoFechar: () => void;
  /** Chamado quando a importação terminou com ao menos um usuário criado. */
  aoImportar?: () => void;
}

const ModalImportarUsuariosCsv: React.FC<ModalImportarUsuariosCsvProps> = ({
  aberto,
  aoFechar,
  aoImportar,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImportacaoCsv[] | null>(null);
  const [erro, setErro] = useState('');

  const limpar = () => {
    setArquivo(null);
    setResultado(null);
    setErro('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const fechar = () => {
    if (importando) return;
    limpar();
    aoFechar();
  };

  const importar = async () => {
    if (!arquivo) return;

    setImportando(true);
    setErro('');

    try {
      const linhas = await importarUsuariosCsv(arquivo);
      setResultado(linhas);

      if (linhas.some(l => l.sucesso)) aoImportar?.();
    } catch (falha: any) {
      setErro(falha?.message ?? 'Não foi possível importar o arquivo.');
    } finally {
      setImportando(false);
    }
  };

  const criados = resultado?.filter(l => l.sucesso).length ?? 0;
  const comErro = resultado?.filter(l => !l.sucesso).length ?? 0;

  return (
    <Modal
      aberto={aberto}
      titulo="Importar usuários por CSV"
      descricao="Cada linha do arquivo vira um consultor, com senha temporária e primeiro acesso obrigatório."
      tamanho="lg"
      ocupado={importando}
      aoFechar={fechar}
      rodape={
        <div className="tw-flex tw-flex-wrap tw-gap-3">
          <Botao variante="secundario" onClick={fechar} disabled={importando}>
            {resultado ? 'Fechar' : 'Cancelar'}
          </Botao>
          {!resultado && (
            <Botao onClick={() => void importar()} carregando={importando} disabled={!arquivo}>
              Importar
            </Botao>
          )}
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!resultado && (
          <>
            <Nota>
              O arquivo precisa ter <strong>cabeçalho</strong> e usar <strong>vírgula</strong> como
              separador, com as colunas: <strong>E-mail</strong>, <strong>CPF/CNPJ</strong>,{' '}
              <strong>Nome</strong>, <strong>Ddd celular</strong>, <strong>Celular</strong>,{' '}
              <strong>Regional</strong>, <strong>Cooperativas</strong> e <strong>Código</strong>.
            </Nota>

            <Nota variante="atencao">
              A coluna <strong>Cooperativas</strong> aceita mais de uma, separadas por vírgula — e
              por isso a célula precisa vir <strong>entre aspas</strong>. Regionais e cooperativas
              são casadas <strong>pelo nome</strong>; o que não casar é ignorado em silêncio, sem
              vínculo. Todos nascem <strong>consultores</strong>, com a senha temporária{' '}
              <strong>securePassword123!</strong> e obrigação de trocá-la no primeiro acesso.
            </Nota>

            <div className="tw-flex tw-flex-col tw-gap-2">
              {/*
                O input fica escondido e é disparado por um botão do kit — o padrão da
                at-63. Exibi-lo cru mostra o widget nativo do navegador, fora da
                identidade visual.
              */}
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="tw-hidden"
                onChange={e => {
                  setArquivo(e.target.files?.[0] ?? null);
                  setErro('');
                }}
              />

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <Botao
                  variante="secundario"
                  iconeEsquerda="documento"
                  onClick={() => inputRef.current?.click()}
                  disabled={importando}
                >
                  Escolher arquivo
                </Botao>

                <span className="tw-text-sm tw-text-texto-corpo">
                  {arquivo ? arquivo.name : 'Nenhum arquivo escolhido'}
                </span>
              </div>
            </div>

            {erro && (
              <p role="alert" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-danger">
                {erro}
              </p>
            )}
          </>
        )}

        {resultado && (
          <>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <Chip variante={criados > 0 ? 'ok' : 'neutro'}>
                {criados} {criados === 1 ? 'usuário criado' : 'usuários criados'}
              </Chip>
              <Chip variante={comErro > 0 ? 'perigo' : 'neutro'}>
                {comErro} {comErro === 1 ? 'linha com erro' : 'linhas com erro'}
              </Chip>
            </div>

            {resultado.length === 0 ? (
              <p className="tw-m-0 tw-text-sm tw-text-texto-suave">
                O arquivo não tinha nenhuma linha de dados.
              </p>
            ) : (
              <DataList
                itens={resultado.map((linha, indice) => ({ ...linha, indice }))}
                chave={l => String(l.indice)}
                rotuloAcessivel="Resultado da importação"
                colunas={[
                  {
                    id: 'email',
                    rotulo: 'E-mail',
                    principal: true,
                    celula: l => l.email ?? 'Linha sem e-mail',
                  },
                  {
                    id: 'situacao',
                    rotulo: 'Situação',
                    celula: l => (
                      <Chip variante={l.sucesso ? 'ok' : 'perigo'}>
                        {l.sucesso ? 'Criado' : 'Não criado'}
                      </Chip>
                    ),
                  },
                  {
                    id: 'erro',
                    rotulo: 'Motivo',
                    secundaria: true,
                    celula: l => l.erro ?? '—',
                  },
                ]}
              />
            )}

            <div className="tw-flex tw-flex-wrap tw-gap-3">
              <Botao variante="secundario" iconeEsquerda="mais" onClick={limpar}>
                Importar outro arquivo
              </Botao>
            </div>
          </>
        )}

        {!resultado && !arquivo && (
          <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-texto-suave">
            <Icone nome="alerta" tamanho={16} />
            Nenhuma linha é criada até você clicar em Importar.
          </p>
        )}
      </div>
    </Modal>
  );
};

export default ModalImportarUsuariosCsv;
